import React from 'react';
import cx from 'classnames';
import Loader from 'components/loader';
import TaskDescriptionSnippet from '../../../../../../javascript/components/TaskDescriptionSnippet';

import { Checkbox, Tooltip } from 'antd';

import styles from '../index.module.css';
import {
  CommentIndicator,
  CommentReadInfo
} from '../../../../../../clients/shared/CommentIndicator/CommentIndicator';
import AvatarList, {
  AssignedUser
} from '../../../../../../clients/shared/AvatarList';
import { accessTo } from 'appJS/models/Access';
import { Sparkles } from '../../../../components/Sparkles';
import ProjectSearchMatches, {
  getMatchedTaskUrl
} from '../../../../components/ProjectSearchMatches';

type Props = {
  task: {
    id: number;
    priority_id: number;
    local_task_id: number;
    description: string;
    description_html?: string | null;
    title: string;
    site?: string | null;
    url?: string | null;
    bulk_request_id?: number;
    comments: CommentReadInfo[];
    ai_suggested_title: boolean;
  };
  getContainer: HTMLDivElement;
  index: number;
  taskEditStatus: boolean;
  groupTotal: number;
  handleTaskClick: (event: React.MouseEvent, taskId: string) => void;
  moveToTaskBoard?: (taskId: number) => void;
  isAdminView: boolean;
  activeTaskId: number;
  taskActive?: boolean;
  isSelected?: boolean;
  assignedUsers: AssignedUser[];
};

const TaskListItem = (props: Props) => {
  const {
    task: {
      id,
      priority_id,
      local_task_id,
      description,
      description_html,
      title,
      site,
      url,
      bulk_request_id,
      comments,
      ai_suggested_title
    },
    getContainer,
    index,
    taskEditStatus,
    groupTotal,
    handleTaskClick,
    moveToTaskBoard,
    isAdminView,
    activeTaskId,
    taskActive,
    isSelected,
    assignedUsers
  } = props;

  const unreadCommentsEnabled = true;

  const currentUserId = window.bugherd?.application.attributes.currentUserId;
  const projectSearchResults = window.bugherd?.application.get(
    'projectSearchResults'
  );
  const searchMatches = projectSearchResults?.matchesByTaskId[id] || [];
  const descriptionSearchHighlight =
    !title && searchMatches.includes('description')
      ? projectSearchResults.query
      : undefined;
  const matchedAssignees =
    projectSearchResults?.matchedAssigneesByTaskId[id] || [];
  const matchedComments =
    projectSearchResults?.matchedCommentsByTaskId[id] || [];
  const pageQuery = projectSearchResults?.pageQuery || '';
  const matchedUrl = getMatchedTaskUrl(searchMatches, site, url, pageQuery);

  return (
    <div
      key={id}
      className={cx(styles.cardContainer, {
        [styles.isListBar]: !isAdminView,
        [styles.lastInGroup]: groupTotal - 1 === index,
        [styles.firstInGroup]: index === 0,
        [styles.isSelected]: isSelected,
        [styles.showAllCheckboxes]: taskActive
      })}
      onClick={event => handleTaskClick(event, id.toString())}
    >
      <div
        className={cx(styles.cardInner, {
          [styles.activeTask]: activeTaskId === id
        })}
      >
        <span className={cx(styles.cardID, styles[`priority${priority_id}`])}>
          {local_task_id}
        </span>
        <div className={styles.cardContent}>
          <div
            className={cx({
              [styles.description]: true,
              [styles.descriptionAndProgress]: isAdminView
            })}
          >
            <Checkbox
              className={styles.checkboxWrapper}
              checked={isSelected}
              key={local_task_id}
            />

            {unreadCommentsEnabled && (
              <CommentIndicator
                comments={comments}
                currentUserId={currentUserId}
              />
            )}

            <div
              className={styles.taskText}
              title={title?.trim() || description?.trim() || undefined}
            >
              {(title || description) !== null ? (
                <TaskDescriptionSnippet
                  {...{
                    title,
                    description,
                    descriptionHtml: description_html,
                    variant: 'list',
                    className: cx(styles.taskDescription, {
                      [styles.withTitle]: title,
                      [styles.aiSuggestedTitle]: title && ai_suggested_title
                    })
                  }}
                  includeGuests={
                    accessTo('assign_guests_allowed') &&
                    accessTo('assign_guests')
                  }
                  searchHighlightText={descriptionSearchHighlight}
                  icon={
                    ai_suggested_title ? (
                      <Tooltip
                        title="AI Suggestion"
                        getPopupContainer={() => getContainer}
                      >
                        <span className={styles.sparklesIconContainer}>
                          <Sparkles
                            className={styles.sparklesIcon}
                            linearGradient
                            size={10}
                          />
                        </span>
                      </Tooltip>
                    ) : null
                  }
                />
              ) : (
                title || description
              )}
              <ProjectSearchMatches
                assignees={matchedAssignees}
                comments={matchedComments}
                includeGuests={
                  accessTo('assign_guests_allowed') && accessTo('assign_guests')
                }
                query={projectSearchResults?.query || ''}
                url={matchedUrl}
                urlQuery={pageQuery || projectSearchResults?.query || ''}
              />
            </div>
            {bulk_request_id && (
              <div className={styles.bulkRequestProgress}>
                <Loader size="small" />
                <span className={styles.archiving}>Archiving...</span>
              </div>
            )}
          </div>
          <div className={styles.cardEnd}>
            {moveToTaskBoard && taskEditStatus && (
              <span
                className={styles.toTaskBoard}
                onClick={event => {
                  event.stopPropagation();
                  moveToTaskBoard(id);
                }}
              >
                move to task board
              </span>
            )}
            {!!assignedUsers.length && (
              <AvatarList assignedUsers={assignedUsers} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskListItem;
